import { environment } from "src/environments/environment";

export type EmbedViewType = "full" | "cover" | "step" | "list";

export type EmbedContentType = "flow" | "bundle";

export interface EmbedContext {
  view: EmbedViewType;
  stepNumber?: number;
}

export interface EmbedSizePreset {
  id: string;
  label: string;
  width: number;
  height: number;
}

export const DEFAULT_EMBED_SIZES: EmbedSizePreset[] = [
  { id: "default", label: "560 × 680", width: 560, height: 680 },
  { id: "compact", label: "426 × 520", width: 426, height: 520 },
  { id: "large", label: "853 × 1040", width: 853, height: 1040 },
];

export function generateUrlSlug(value: string): string {
  if (!value) {
    return "flow";
  }
  return value
    .replace(/<[^>]*>/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();
}

export function getEmbedBaseUrl(): string {
  const configured = (environment.webBaseUrl || "").replace(/\/$/, "");
  if (configured) {
    return configured;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://app.flowchest.com";
}

/** Use the running app origin so share-dialog preview matches the current environment. */
export function getEmbedPreviewBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return getEmbedBaseUrl();
}

export function buildEmbedUrl(
  contentType: EmbedContentType,
  rowKey: string,
  title: string,
  view: EmbedViewType = "full",
  stepNumber?: number,
  baseUrl?: string
): string {
  const slug = generateUrlSlug(title || "content");
  const base = (baseUrl || getEmbedBaseUrl()).replace(/\/$/, "");
  const path =
    contentType === "bundle"
      ? `/embed/guide-pack/${slug}/${rowKey}`
      : `/embed/guide/${slug}/${rowKey}`;

  const params = new URLSearchParams();
  params.set("view", view);
  if (view === "step" && stepNumber && stepNumber > 0) {
    params.set("step", String(stepNumber));
  }

  return `${base}${path}?${params.toString()}`;
}

export function buildIframeEmbedCode(
  embedUrl: string,
  width: number,
  height: number
): string {
  return `<iframe src="${embedUrl}" width="${width}" height="${height}" frameborder="0" allowfullscreen title="Flowchest embed"></iframe>`;
}

export function buildEmbedContextFromFlowState(
  selectedStepIndex: number,
  itemsRequired: number
): EmbedContext {
  if (selectedStepIndex === -2) {
    return { view: "cover" };
  }
  if (selectedStepIndex === -1 || itemsRequired === 1) {
    return { view: "list" };
  }
  if (selectedStepIndex >= 0) {
    return { view: "step", stepNumber: selectedStepIndex + 1 };
  }
  return { view: "full" };
}

export function getEmbedViewOptions(
  contentType: EmbedContentType,
  stepCount = 0,
  hasItems = false
): { id: EmbedViewType; label: string; stepNumber?: number }[] {
  if (contentType === "bundle") {
    return [
      { id: "full", label: "Full bundle" },
      { id: "cover", label: "Cover only" },
    ];
  }

  const options: { id: EmbedViewType; label: string; stepNumber?: number }[] = [
    { id: "full", label: "Full flow (with steps)" },
    { id: "cover", label: "Cover only" },
  ];

  if (hasItems) {
    options.push({ id: "list", label: "Items list only" });
  }

  for (let i = 1; i <= stepCount; i++) {
    options.push({ id: "step", label: `Step ${i} only`, stepNumber: i });
  }

  return options;
}

export function buildServerIframeEmbedCode(
  embedUrl: string,
  width = 560,
  height = 680
): string {
  return buildIframeEmbedCode(embedUrl, width, height);
}

export interface ParsedIframeEmbed {
  src: string;
  width: number;
  height: number;
}

export function parseIframeEmbedCode(
  embedCode?: string | null
): ParsedIframeEmbed | null {
  if (!embedCode?.trim()) {
    return null;
  }

  const srcMatch = embedCode.match(/src=["']([^"']+)["']/i);
  if (!srcMatch?.[1]) {
    return null;
  }

  const widthMatch = embedCode.match(/width=["']?(\d+)["']?/i);
  const heightMatch = embedCode.match(/height=["']?(\d+)["']?/i);

  return {
    src: srcMatch[1],
    width: widthMatch ? parseInt(widthMatch[1], 10) : DEFAULT_EMBED_SIZES[0].width,
    height: heightMatch
      ? parseInt(heightMatch[1], 10)
      : DEFAULT_EMBED_SIZES[0].height,
  };
}
